import { config } from '@/config/env'

/**
 * Silent renewal of the app session.
 *
 * The access token is short-lived now (15 minutes), so something has to renew
 * it before it expires or every user is bounced to SecureID four times an hour.
 *
 * WHY A PROACTIVE TIMER RATHER THAN A 401 INTERCEPTOR: there is no central HTTP
 * client on the web. Eight feature services each build their own fetch and read
 * the token straight out of localStorage, synchronously. Retrofitting an
 * interceptor would mean rewriting all eight and making every call site async.
 * Instead we keep the STORED session fresh: this module rewrites the same
 * localStorage blob those services already read, so they keep working unchanged
 * and simply never see an expired token. `refreshNow()` is exported for the
 * cases that do want to await a renewal.
 *
 * The refresh credential is the HttpOnly `inmeet_session` cookie, which is why
 * every request here sends `credentials: 'include'` and no token is passed in
 * JS. The cookie is unreadable from this file by design (guide section 12).
 */

const STORAGE_KEY = 'inmeet_auth_session'

/** Renew this long before expiry, so a slow network doesn't race the deadline. */
const RENEW_MARGIN_MS = 60_000
/** Never schedule tighter than this — guards against a hot loop on odd clocks. */
const MIN_DELAY_MS = 5_000
/** Backoff for infrastructure failures, which must NOT sign the user out. */
const RETRY_BASE_MS = 15_000
const RETRY_MAX_MS = 5 * 60_000

export const REAUTH_EVENT = 'inmeet:reauth-required'

/**
 * A renewal landed and the stored blob has been rewritten.
 *
 * Announced because this module is deliberately storage-level: it keeps the
 * localStorage copy fresh so the eight feature services that read it directly
 * keep working. The route guard does NOT read that copy — it reads the auth
 * store in memory, which nothing here can reach without importing a feature.
 * So the store went on holding the expiry issued at login, and fifteen minutes
 * later the guard redirected a perfectly valid session to /login on the next
 * navigation. AuthHydrator listens for this and applies it, the same way it
 * already listens for REAUTH_EVENT.
 */
export const SESSION_REFRESHED_EVENT = 'inmeet:session-refreshed'

type StoredSession = {
  session: { accessToken: string; refreshToken: string; expiresAt: number }
  user: { id: string; email: string; name: string; avatarUrl?: string; role?: string }
  presenceId?: string | null
}

let timer: ReturnType<typeof setTimeout> | null = null
let inFlight: Promise<boolean> | null = null
let consecutiveFailures = 0

type RenewedSession = {
  accessToken: string
  expiresIn: number
  user?: { id: string; email: string; name: string; avatarUrl?: string; role?: string }
}

/**
 * Where renewal comes from when it is not the session cookie.
 *
 * A meeting framed by another application cannot rely on the HttpOnly cookie —
 * it is a third-party cookie there, and Safari never sends it. The embed layer
 * installs a renewer that asks the host application for a fresh SecureID token
 * and exchanges it. `'reauth'` is terminal (the host has no signed-in user any
 * more); `'retry'` is the host being slow, which must not sign anyone out.
 * Null — always, outside a frame — means the cookie path below, unchanged.
 */
type SessionRenewer = () => Promise<RenewedSession | 'reauth' | 'retry'>
let renewer: SessionRenewer | null = null

export function setSessionRenewer(next: SessionRenewer | null): void {
  renewer = next
}

function readStored(): StoredSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredSession
    return parsed?.session && parsed?.user ? parsed : null
  } catch {
    return null
  }
}

function writeStored(next: StoredSession): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
}

/**
 * Silent renewal is over — the refresh token is expired, revoked, or was
 * replayed. Guide section 10: send the user through authorization again.
 */
function announceReauthRequired(): void {
  stopSessionRefresh()
  localStorage.removeItem(STORAGE_KEY)
  window.dispatchEvent(new CustomEvent(REAUTH_EVENT))
}

/** Long enough for a transient miss to pass, short enough not to be a hang. */
const NO_COOKIE_RETRY_MS = 1000

function postRefresh(): Promise<Response> {
  return fetch(`${config.apiUrl}/api/v1/auth/refresh`, {
    method: 'POST',
    // The session cookie IS the credential; nothing is sent in the body.
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  })
}

/**
 * The server's error code, or null if the body is not the shape we expect.
 *
 * Reads a CLONE: the caller may still need the body, and a Response can only be
 * consumed once.
 */
async function errorCode(res: Response): Promise<string | null> {
  try {
    const body = (await res.clone().json()) as { error?: { code?: unknown } }
    return typeof body?.error?.code === 'string' ? body.error.code : null
  } catch {
    return null
  }
}

/**
 * Renew now. Single-flight: concurrent callers share one request.
 *
 * That matters beyond saving a round-trip. Every refresh rotates the token, and
 * guide section 7 says presenting a rotated one looks like a stolen-token
 * replay. The backend tolerates a genuine race through its grace window, but
 * "do not rely on it" is the guide's own advice — so we simply don't produce
 * races from this tab.
 */

export function refreshNow(): Promise<boolean> {
  if (inFlight) return inFlight

  inFlight = withRefreshLock(async () => {
    // Another TAB may have refreshed while we waited for the lock. Adopting its
    // result is not just an optimisation: the token it rotated to is the only
    // live one, and calling again with what we still hold would present a token
    // SecureID has already retired — the replay the guide's section 7 warns
    // ends the whole session.
    if (!accessTokenSpent()) {
      scheduleSessionRefresh()
      return true
    }

    try {
      const data = renewer ? await renewer() : await renewWithCookie()
      if (data === 'reauth') {
        announceReauthRequired()
        return false
      }
      if (data === 'retry') {
        scheduleRetry()
        return false
      }

      const stored = readStored()
      if (!stored) {
        // Signed out in another tab while this was in flight.
        stopSessionRefresh()
        return false
      }

      writeStored({
        ...stored,
        session: {
          ...stored.session,
          accessToken: data.accessToken,
          expiresAt: Date.now() + data.expiresIn * 1000,
        },
        // Role or org may have changed since login; the backend re-reads the
        // user on every refresh, so take its answer.
        user: data.user ? { ...stored.user, ...data.user } : stored.user,
      })

      window.dispatchEvent(
        new CustomEvent(SESSION_REFRESHED_EVENT, {
          detail: {
            session: {
              ...stored.session,
              accessToken: data.accessToken,
              expiresAt: Date.now() + data.expiresIn * 1000,
            },
            user: data.user,
          },
        }),
      )

      consecutiveFailures = 0
      scheduleSessionRefresh()
      return true
    } catch {
      // Offline or DNS failure — same reasoning as a 503.
      scheduleRetry()
      return false
    }
  }).finally(() => {
    inFlight = null
  })

  return inFlight
}

/** The default renewal: the HttpOnly session cookie. */
async function renewWithCookie(): Promise<RenewedSession | 'reauth' | 'retry'> {
  let res = await postRefresh()

  // The browser sent no cookie. That is NOT the same as a dead session.
  //
  // The session token is an HttpOnly cookie, which this code cannot read,
  // so "no cookie arrived" tells us nothing about whether the session on
  // the server is alive — and in production it demonstrably was: refreshes
  // succeeded every fourteen minutes either side of the one that failed.
  // Treating it as terminal meant a single dropped cookie ended a healthy
  // thirty-day session and dumped the user at the login screen.
  //
  // One retry, because the only way to find out is to ask again. If the
  // cookie is genuinely gone the second attempt comes back the same way and
  // we sign out then — a second late, and correct.
  if (res.status === 401 && (await errorCode(res)) === 'NO_SESSION_TOKEN') {
    await new Promise((resolve) => setTimeout(resolve, NO_COOKIE_RETRY_MS))
    res = await postRefresh()
  }

  if (res.status === 401) return 'reauth'

  // 503 REFRESH_UNAVAILABLE and friends: the session is probably fine,
  // the backend or SecureID is not. Retrying beats signing the user out.
  if (!res.ok) return 'retry'

  return (await res.json()) as RenewedSession
}

/**
 * Serialise renewal across TABS, not just within one.
 *
 * `inFlight` above is a module variable, so it only ever guarded a single tab.
 * Every tab reads the same `expiresAt` out of shared localStorage and therefore
 * schedules the same deadline, so three open tabs fired three refreshes
 * milliseconds apart — 13% of production refreshes were landing in the
 * backend's concurrency grace window. Nothing broke, because that window caught
 * them, but the guide is explicit that it must not be relied on: outside its
 * ~20 seconds a straggler looks like a stolen-token replay and the session is
 * revoked. A sleeping laptop or a slow network is all it would take.
 *
 * Web Locks are genuinely atomic across tabs of an origin. Where they are
 * missing the call simply proceeds — the previous behaviour, still covered by
 * the grace window — rather than inventing a localStorage lock whose
 * read-then-write is itself a race.
 */
function withRefreshLock(fn: () => Promise<boolean>): Promise<boolean> {
  const locks = (navigator as Navigator & { locks?: LockManager }).locks
  if (!locks?.request) return fn()
  return locks.request('inmeet-auth-refresh', fn) as Promise<boolean>
}

/**
 * Is the stored access token spent (or about to be), so the app must renew
 * before it can make its first authenticated call?
 *
 * True does NOT mean signed out. The refresh credential is the HttpOnly cookie,
 * good for thirty days; this only asks whether the fifteen-minute access token
 * in storage is still usable. The boot path awaits a renewal when it isn't,
 * because the eight feature services read that token straight out of
 * localStorage and would otherwise spend the first seconds of the session
 * firing requests with a dead one.
 */
export function accessTokenSpent(): boolean {
  const stored = readStored()
  if (!stored) return false
  return stored.session.expiresAt - Date.now() <= RENEW_MARGIN_MS
}

function scheduleRetry(): void {
  consecutiveFailures += 1
  const delay = Math.min(RETRY_BASE_MS * 2 ** (consecutiveFailures - 1), RETRY_MAX_MS)
  if (timer) clearTimeout(timer)
  timer = setTimeout(() => void refreshNow(), delay)
}

/**
 * Arm the renewal timer from the stored expiry. Idempotent — safe to call on
 * every login, hydrate and successful refresh.
 */
export function scheduleSessionRefresh(): void {
  if (timer) clearTimeout(timer)
  timer = null

  const stored = readStored()
  if (!stored) return

  const delay = Math.max(stored.session.expiresAt - Date.now() - RENEW_MARGIN_MS, MIN_DELAY_MS)
  timer = setTimeout(() => void refreshNow(), delay)
}

export function stopSessionRefresh(): void {
  if (timer) clearTimeout(timer)
  timer = null
  consecutiveFailures = 0
}

/**
 * Adopt what another tab did to the session, instead of racing it.
 *
 * The `storage` event fires only in the OTHER tabs, which is exactly the
 * audience that needs telling. Two things travel this way:
 *
 *   • A renewal — reschedule from the new expiry and put the new token into
 *     this tab's in-memory store, so its route guard and feature services agree
 *     with the blob they share. Without this the other tabs kept their own
 *     timers pointed at the OLD deadline and each woke up to refresh a token
 *     that had already been rotated.
 *   • A sign-out — the key is gone, so this tab is signed out too. Previously a
 *     logout in one tab left the others believing they were signed in until
 *     their own refresh eventually failed.
 *
 * A removal is re-read before being believed: `sessionStorageRepo.save()`
 * removes the key and immediately re-sets it, so a LOGIN briefly looks like a
 * sign-out. Both writes land in one task before any event is delivered, so by
 * the time this runs the current value is the honest answer.
 */
export function installCrossTabSync(): () => void {
  const onStorage = (e: StorageEvent) => {
    if (e.key !== STORAGE_KEY) return

    const stored = readStored()
    if (!stored) {
      // Genuinely gone — signed out elsewhere.
      stopSessionRefresh()
      window.dispatchEvent(new CustomEvent(REAUTH_EVENT))
      return
    }

    scheduleSessionRefresh()
    window.dispatchEvent(
      new CustomEvent(SESSION_REFRESHED_EVENT, {
        detail: { session: stored.session, user: stored.user },
      }),
    )
  }

  window.addEventListener('storage', onStorage)
  return () => window.removeEventListener('storage', onStorage)
}

/**
 * The stored token, renewed first if it is at or past the margin.
 *
 * For callers that can await. Everything reading localStorage synchronously is
 * served by the timer above instead.
 */
export async function ensureFreshAccessToken(): Promise<string> {
  const stored = readStored()
  if (!stored) return ''
  if (stored.session.expiresAt - Date.now() > RENEW_MARGIN_MS) return stored.session.accessToken

  await refreshNow()
  return readStored()?.session.accessToken ?? ''
}

/**
 * Renew when the tab wakes up.
 *
 * setTimeout does not fire reliably in a backgrounded or sleeping tab, so a
 * laptop reopened after an hour would otherwise come back holding an expired
 * token and every request would 401 before the timer caught up.
 */
export function installVisibilityRefresh(): () => void {
  const onVisible = () => {
    if (document.visibilityState !== 'visible') return
    const stored = readStored()
    if (!stored) return
    if (stored.session.expiresAt - Date.now() <= RENEW_MARGIN_MS) void refreshNow()
    else scheduleSessionRefresh()
  }

  document.addEventListener('visibilitychange', onVisible)
  window.addEventListener('online', onVisible)
  return () => {
    document.removeEventListener('visibilitychange', onVisible)
    window.removeEventListener('online', onVisible)
  }
}
